/**
 * MSAL Initialization Wrapper Component
 *
 * Wraps the application to ensure MSAL is initialized before rendering children.
 * This component:
 * 1. Waits for ConfigProvider to load Azure AD configuration from YAML
 * 2. Initializes PublicClientApplication with loaded configuration
 * 3. Wraps children with MsalProvider for hook access
 * 4. Prevents double-initialization with useRef
 * 5. Shows loading spinner while initializing
 * 6. Handles errors with retry option
 *
 * Provider Nesting Order:
 * ConfigProvider → MsalWrapper → AuthProvider → ApiQueryServiceProvider → RouterProvider
 */

import React, { useEffect, useRef, useState } from 'react'
import { MsalProvider } from '@azure/msal-react'
import { getMsalInstance } from '@/authConfig'
import { useConfig } from '@/contexts/ConfigContext'
import { AppLogger } from '@/lib/logger'
import { Loader2 } from 'lucide-react'

const log = AppLogger.getLogger(import.meta)

// Module-level storage for MSAL redirect response (avoids sessionStorage token exposure)
let pendingRedirectResponse: import('@azure/msal-browser').AuthenticationResult | null = null

export function getPendingRedirectResponse(): import('@azure/msal-browser').AuthenticationResult | null {
  const response = pendingRedirectResponse
  pendingRedirectResponse = null
  return response
}

/**
 * Props for MsalWrapper component
 */
interface MsalWrapperProps {
  children: React.ReactNode
}

/**
 * MSAL Initialization Wrapper Component
 *
 * Ensures MSAL is initialized with loaded configuration before rendering application.
 * Shows loading state during initialization and error states with retry capability.
 *
 * @param children - React components to wrap (typically AuthProvider and RouterProvider)
 * @returns Initialized MsalProvider or loading/error state
 */
export function MsalWrapper({ children }: MsalWrapperProps) {
  const config = useConfig()
  const [isInitialized, setIsInitialized] = useState(false)
  const [initError, setInitError] = useState<Error | null>(null)
  const initializeRef = useRef(false)

  useEffect(() => {
    // Skip if already initialized or currently initializing
    if (initializeRef.current || isInitialized) {
      return
    }

    // Wait for ConfigProvider to finish loading
    if (config.isLoading) {
      return
    }

    // Check for ConfigProvider loading errors
    if (config.error) {
      setInitError(config.error)
      log.error('ConfigProvider loading error:', config.error)
      return
    }

    // Check for authentication configuration
    if (!config.authConfig) {
      const error = new Error(
        'Authentication configuration not found. ' +
          'Ensure auth keys exist in environment.yml.'
      )
      setInitError(error)
      log.error('No authentication config:', error)
      return
    }

    // Mark as initializing to prevent double-init
    initializeRef.current = true

    const initMsal = async () => {
      try {
        const authConfig = config.authConfig
        const clientId = authConfig?.authentication?.azure?.clientId
        const tenantId = authConfig?.authentication?.azure?.tenantId
        const redirectUri = authConfig?.authentication?.azure?.redirectUri

        log.info(
          `MSAL initializing: clientId=${clientId?.slice(0, 8)}..., tenantId=${tenantId?.slice(0, 8)}..., redirectUri=${redirectUri}`,
          { authConfig }
        )

        // Create/get MSAL instance with loaded configuration
        const instance = getMsalInstance(authConfig ?? undefined)

        // Initialize MSAL with loaded configuration
        await instance.initialize()

        // Handle any pending redirect authentication responses
        // This is called after returning from Microsoft login redirect
        log.info('Calling handleRedirectPromise...')
        try {
          const redirectResponse = await instance.handleRedirectPromise()

          if (redirectResponse) {
            log.info('handleRedirectPromise returned a response (redirect auth completed)', {
              account: redirectResponse.account?.username,
            })
            // Store in module-level variable for AuthContext to pick up on mount
            // Avoids exposing tokens in sessionStorage
            pendingRedirectResponse = redirectResponse
            try {
              const prev = JSON.parse(window.localStorage.getItem('__conexMsalLog') ?? '[]')
              prev.push({ result: 'response', href: window.location.href, account: redirectResponse.account?.username, ts: Date.now() })
              window.localStorage.setItem('__conexMsalLog', JSON.stringify(prev.slice(-5)))
            } catch { /* ignore */ }
          } else {
            log.info('handleRedirectPromise returned null (no pending auth response)')
            try {
              const prev = JSON.parse(window.localStorage.getItem('__conexMsalLog') ?? '[]')
              prev.push({ result: 'null', href: window.location.href, hash: window.location.hash, search: window.location.search, ts: Date.now() })
              window.localStorage.setItem('__conexMsalLog', JSON.stringify(prev.slice(-5)))
            } catch { /* ignore */ }
          }
        } catch (redirectError) {
          // handleRedirectPromise can fail with stale/invalid redirect responses
          // (e.g. AADSTS65005 scope errors from a previous login attempt).
          // Log the error but still mark MSAL as initialized so the app can render
          // and the user can re-attempt login.
          log.warn('handleRedirectPromise failed (stale redirect response cleared):', redirectError)
          const errMsg = redirectError instanceof Error ? redirectError.message : String(redirectError)
          try {
            const prev = JSON.parse(window.localStorage.getItem('__conexMsalLog') ?? '[]')
            prev.push({ result: 'error', href: window.location.href, error: errMsg, ts: Date.now() })
            window.localStorage.setItem('__conexMsalLog', JSON.stringify(prev.slice(-5)))
          } catch { /* ignore */ }
        }

        setIsInitialized(true)

        log.info(`MSAL initialized successfully with instance for ${tenantId?.slice(0, 8)}...`)
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error('MSAL initialization failed: unknown error')

        log.error('MSAL initialization error:', err)
        setInitError(err)
        initializeRef.current = false // Allow retry
      }
    }

    initMsal()
  }, [config.isLoading, config.error, config.authConfig, isInitialized])

  // Show loading spinner while waiting for ConfigProvider or MSAL init
  // Skip the loading spinner if there's an init error so we fall through to the error UI
  if ((config.isLoading || !isInitialized) && !initError) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">Initializing authentication...</div>
            <div className="text-sm text-gray-500">Setting up Microsoft Authentication</div>
          </div>
        </div>
      </div>
    )
  }

  // Show error if config failed to load
  if (config.error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="max-w-md text-center">
          <div className="mb-4 text-lg font-semibold text-red-600">Configuration Error</div>
          <div className="mb-6 text-sm text-gray-600">{config.error.message}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Show error if authentication config is missing
  if (!config.authConfig) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="max-w-md text-center">
          <div className="mb-4 text-lg font-semibold text-red-600">Configuration Error</div>
          <div className="mb-6 text-sm text-gray-600">
            Authentication configuration not found. Check environment.yml auth values.
          </div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Show error if MSAL initialization failed
  if (initError) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="max-w-md text-center">
          <div className="mb-4 text-lg font-semibold text-red-600">Authentication Error</div>
          <div className="mb-6 text-sm text-gray-600">{initError.message}</div>
          <button
            onClick={() => {
              setInitError(null)
              initializeRef.current = false
              setIsInitialized(false)
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Render children wrapped with MsalProvider (enables useMsal() hook)
  // At this point, authConfig is guaranteed to be non-null due to earlier checks
  const msalInstance = getMsalInstance(config.authConfig as any)
  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>
}
